import { useEffect, useState } from 'react';
import PatientTypeLeaderboard from './PatientTypeLeaderboard';
import { getPatientTypeBreakdown } from '../../../utils/functions';
import { format } from 'date-fns';

export default function PatientTypeBreakdown({ patientTypes, setPatientTypes, setOpenPatientsModal, org }) {
  const [openPatientsLeaderboard, setOpenPatientsLeaderboard] = useState(false);
  const todaysDate = format(new Date(), 'yyyy-MM-dd');
  const [isLoading, setIsLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: format(new Date(), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd')
  });

  const handleClose = () => {
    setOpenPatientsModal(false);
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const renderPercent = (seen, total) => {
    if (total === 0) {
      return 0;
    } else {
      return ((seen / total) * 100).toFixed(2);
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      let queryString = '';
      if (dateRange.startDate && dateRange.endDate) {
        queryString = `?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`;
      } else if (dateRange.startDate) {
        queryString = `?startDate=${dateRange.startDate}`;
      }

      const updatedStats = await getPatientTypeBreakdown(org, queryString);
      setPatientTypes(updatedStats);
    } catch (error) {
      console.error('Error updating date range:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === todaysDate) return 'Today';
    return format(new Date(dateString + 'T00:00:00'), 'MMM dd, yyyy');
  };
  
  return (
    <>
      {patientTypes !== null ?
        (<div className="fixed left-[28%] top-[28%] w-[50%] flex flex-col items-center justify-center bg-light-purple p-4 rounded-md shadow-md z-10">
          <button className='absolute right-[20px] top-[10px] hover:text-ktx-navy font-bold text-[26px]' onClick={() => handleClose()}>×</button>
          <h2 className="font-semibold text-[26px]">Patient Type Breakdown for {org}</h2>
          <form onSubmit={handleSubmit} className="w-full max-w-2xl mb-4">
            <div className="flex items-end gap-4 justify-center m2-4">
              <div className="flex flex-col">
                <label className="text-sm text-ktx-navy mb-1">Start Date</label>
                <input
                  type="date"
                  name="startDate"
                  value={dateRange.startDate}
                  onChange={handleInputChange}
                  className="rounded-md border-gray-200 p-2"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-sm text-ktx-navy mb-1">End Date</label>
                <input
                  type="date"
                  name="endDate"
                  value={dateRange.endDate}
                  onChange={handleInputChange}
                  className="rounded-md border-gray-200 p-2"
                />
              </div>
              <button
                type="submit"
                className="bg-ktx-purple text-white px-6 py-2 rounded-md hover:bg-ktx-navy h-[2.5rem]"
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="inline-flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Loading...
                  </span>
                ) : 'Update'}
              </button>
            </div>
          </form>
          {dateRange.startDate === todaysDate && dateRange.endDate === todaysDate ? <p className="text-sm text-ktx-navy mb-2">Showing data from Today</p>
            :
            <p className="text-sm text-ktx-navy mb-2">
              Showing data from {formatDate(dateRange.startDate)} to {formatDate(dateRange.endDate)}
            </p>
          }

          <button
            onClick={() => setOpenPatientsLeaderboard(true)}
            classfName="mb-4 bg-ktx-purple text-white px-4 py-2 rounded-md hover:bg-ktx-navy"
          >
            Show Leaderboard
          </button>
          {openPatientsLeaderboard && <PatientTypeLeaderboard leaderboardInfo={patientTypes.doctorStats} setOpenPatientsLeaderboard={setOpenPatientsLeaderboard} org={org} />}
          <table className="w-full rounded-md">
            <thead>
              <tr>
                <th className="border-b-[2px] border-b-ktx-purple">Patient Type</th>
                <th className="border-b-[2px] border-b-ktx-purple">Count (Seen/Total)</th>
                <th className="border-b-[2px] border-b-ktx-purple">Utilization Percent</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-dark-purple text-white">
                <td className="p-2 border-b border-gray-200 border-b-ktx-navy">Total Patients</td>
                <td className="p-2 border-b border-gray-200  border-b-ktx-navy text-center">{patientTypes.totalSeen}/{patientTypes.total}</td>
                <td className="p-2 border-b border-gray-200  border-b-ktx-navy text-center">{renderPercent(patientTypes.totalSeen, patientTypes.total)}%</td>
              </tr>
              <tr className="bg-white">
                <td className="p-2 border-b border-gray-200">New Patients</td>
                <td className="p-2 border-b border-gray-200 text-center">{patientTypes.newPatientsSeen}/{patientTypes.newPatients}</td>
                <td className="p-2 border-b border-gray-200 text-center">{renderPercent(patientTypes.newPatientsSeen, patientTypes.newPatients)}%</td>
              </tr>
              <tr className="bg-white">
                <td className="p-2 border-b border-gray-200">Follow-Up Patients</td>
                <td className="p-2 border-b border-gray-200 text-center">{patientTypes.followUpPatientsSeen}/{patientTypes.followUpPatients}</td>
                <td className="p-2 border-b border-gray-200 text-center">{renderPercent(patientTypes.followUpPatientsSeen, patientTypes.followUpPatients)}%</td>
              </tr>
              <tr className="bg-white">
                <td className="p-2">Misc</td>
                <td className="p-2 text-center">{patientTypes.unallocatedPatientsSeen}/{patientTypes.unallocatedPatients}</td>
                <td className="p-2 text-center">{renderPercent(patientTypes.unallocatedPatientsSeen, patientTypes.unallocatedPatients)}%</td>
              </tr>
            </tbody>
          </table>
        </div>)
        :
        (<span className="w-6 h-6 border-2 border-dashed rounded-full animate-spin border-ktx-purple"></span>)}
    </>
  )
}